<script setup lang="ts">
export interface Props {
  width?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "auto" | "full";
  // Fixed tokens (xs..xl) and "auto" cap the popup's height. "content" removes the cap so the
  // popup grows to its content — use it for content that manages its own height (e.g. an inner
  // max-height + scroll). In a DropdownMenu, "content" additionally disables the viewport
  // height-clamp (see DropdownMenu's maxHeight prop / usePopper), so the content is never
  // squeezed to the space around the trigger.
  maxHeight?: "xs" | "sm" | "md" | "lg" | "xl" | "auto" | "content";
}

withDefaults(defineProps<Props>(), { width: "auto", maxHeight: "auto" });
</script>

<template>
  <div :class="['base-popup', `w-${width}`, `max-h-${maxHeight}`]">
    <slot />
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/font";
@use "@/styles/mixins/shadow";

.base-popup {
  $max-height: (
    "xs": 250px,
    "sm": 300px,
    "md": 350px,
    "lg": 450px,
    "xl": 550px,
    "auto": auto,
    // No cap: the popup sizes to its content (which is expected to manage its own height).
    "content": none,
  );
  $width: (
    "xs": 120px,
    "sm": 200px,
    "md": 260px,
    "lg": 320px,
    "xl": 400px,
    "xxl": 700px,
    "auto": fit-content,
    "full": 100%,
  );

  @include shadow.default;

  display: flex;
  flex-direction: column;
  background-color: colors.$background-ui;
  color: colors.$main;
  border-radius: 8px;
  overflow: hidden;
  font-size: 14px;
  font-weight: font.$weight-semi-bold;

  @each $key, $value in $max-height {
    &.max-h-#{$key} {
      @if $key == "auto" {
        max-height: var(--popper-max-height, none);
      } @else if $key == "content" {
        max-height: $value;
      } @else {
        max-height: min(#{$value}, var(--popper-max-height, #{$value}));
      }
    }
  }

  @each $key, $value in $width {
    &.w-#{$key} {
      width: $value;
    }
  }
}
</style>
